import { useEffect } from 'react'

import { create, toBinary } from '@bufbuild/protobuf'
import { Code } from '@connectrpc/connect'
import { UpdatePlantMasterRequestSchema } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { AreaChangeConfirmDialog } from '~/components/common/AreaChangeConfirmDialog'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import type { PlantMasterRow } from '~/hooks/useListPlantMasters'
import {
  type AreaChangeConfirmation,
  plantAreaChangeConfirmationOf,
} from '~/models/areaChange'
import { todayCivilDate } from '~/models/civilDate'
import { decimalToNumber } from '~/models/decimal'
import { PlantMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/components/PlantMasterFormFields'
import { useGetPlantMaster } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useGetPlantMaster'
import { useUpdatePlantMaster } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useUpdatePlantMaster'
import {
  type PlantMasterFormInput,
  PlantMasterFormSchema,
  type PlantMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/schema'
import { resolvePlantMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/serverError'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: PlantMasterRow
  onClose: () => void
}

const PLANT_MASTER_EDIT_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.Aborted]: 'page:adminOcctoMaster.plantMaster.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.plantMaster.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.plantMaster.serverError.invalidArgument',
}

/**
 * 発電所マスタ編集モーダル。
 *
 * `useCrudModalForm` + `CrudModalLayout` に集約 (他 7 タブと同 pattern)。
 * useGetPlantMaster で full entity (members 含む) を取り直してから form を populate する。
 * plantCode は readonly (AC-006)。エリアは選び直せ、変更は backend の歯止め (登録先があれば止める・
 * 食い違うユニットがあれば確認) を通る。
 * 監査情報 (AuditPanel) は Phase 1 では非表示 (issue #3221)。
 * optimistic locking は Get response の updated_at を expected_updated_at として送る。
 */
export const EditPlantMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdatePlantMaster()
  const {
    data,
    isPending: isFetching,
    isError,
    error,
  } = useGetPlantMaster(row.id)
  const fetched = data?.plantMaster
  const {
    confirmation,
    confirm,
    cancelConfirmation,
    isSubmitting,
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    reset,
    setValue,
    isDirty,
  } = useCrudModalForm<
    PlantMasterFormInput,
    PlantMasterFormValues,
    AreaChangeConfirmation
  >({
    schema: PlantMasterFormSchema,
    defaultValues: {
      plantCode: row.plantCode,
      plantName: row.plantName,
      plantNameAbbr: row.plantNameAbbr,
      areaCode: row.areaCode,
      powerType: row.powerType,
      voltageClass: row.voltageClass,
      maxReceivingPowerKw: '',
      businessMasterId: row.businessMasterId,
      contractIdNo2: '',
      priority: row.priority,
      proRataPriority: undefined,
      validFrom: row.validFrom,
      validTo: undefined,
      note: '',
      members: [{ businessMasterId: row.businessMasterId, isOwn: false }],
    },
    confirmationOf: (err) => plantAreaChangeConfirmationOf(t, err),
    prepareSubmit: (values) => {
      if (fetched?.updatedAt === undefined) {
        // 通常は isFetching の間 save ボタンが disabled なので到達しない
        throw new Error(
          t('page:adminOcctoMaster.plantMaster.serverError.unknown'),
        )
      }
      const request = create(UpdatePlantMasterRequestSchema, {
        id: row.id,
        plantName: values.plantName,
        plantNameAbbr: values.plantNameAbbr,
        areaCode: values.areaCode,
        areaChangeConfirmed: false,
        powerType: values.powerType,
        voltageClass: values.voltageClass,
        // 必須 (schema で非空 + 0 以上を検証済み) のため常に Decimal を組む。
        maxReceivingPowerKw: create(DecimalSchema, {
          value: values.maxReceivingPowerKw,
        }),
        businessMasterId: values.businessMasterId,
        contractIdNo2:
          values.contractIdNo2 !== undefined && values.contractIdNo2 !== ''
            ? values.contractIdNo2
            : undefined,
        priority: values.priority,
        proRataPriority: values.proRataPriority,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // member の有効期間は plant の適用期間を継承する (proto PlantMemberInput
        // .valid_from は required。 画面は member 個別の日付入力を持たないため、
        // plant の validFrom / validTo を全 member に適用する)。
        members: values.members.map((m) => ({
          businessMasterId: m.businessMasterId,
          isOwn: m.isOwn,
          validFrom: {
            year: values.validFrom.year,
            month: values.validFrom.month,
            day: values.validFrom.day,
          },
          validTo:
            values.validTo !== undefined
              ? {
                  year: values.validTo.year,
                  month: values.validTo.month,
                  day: values.validTo.day,
                }
              : undefined,
        })),
        // valid_from は update request で required (proto field 15, mutable。
        // backend PlantMaster.WithUpdatedMutableFields も valid_from を受ける)。
        // 送信し忘れると BFF protovalidate が InvalidArgument を返し編集不可に
        // なる (issue #5913 と同型)。
        validFrom: {
          year: values.validFrom.year,
          month: values.validFrom.month,
          day: values.validFrom.day,
        },
        validTo:
          values.validTo !== undefined
            ? {
                year: values.validTo.year,
                month: values.validTo.month,
                day: values.validTo.day,
              }
            : undefined,
        // fetched.updatedAt は proto Timestamp をそのまま round-trip。
        // JS Date を経由すると µs 精度が落ち、 backend の DB updated_at (= µs 精度)
        // との optimistic-lock 比較が一致せず Aborted で返る。
        expectedUpdatedAt: fetched.updatedAt,
      })
      return {
        identity: toBinary(UpdatePlantMasterRequestSchema, request),
        send: (areaChangeConfirmed) =>
          mutateAsync({ ...request, areaChangeConfirmed }),
      }
    },
    onClose,
    customErrorKeys: PLANT_MASTER_EDIT_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.plantMaster.serverError.unknown',
    resolveDetailedError: resolvePlantMasterDetailedError,
  })

  // Get response が返ったら form を populate (= 別行を編集した場合も含む)
  useEffect(() => {
    if (fetched !== undefined) {
      // 代表事業者 (businessMasterId と一致する行) を先頭に並べ替える
      // (form fields 側で members[0] = 代表同期行として扱うため、 BgMaster と同じ方式)
      const orderedMembers = fetched.members.map((m) => ({
        businessMasterId: m.businessMasterId,
        isOwn: m.isOwn,
      }))
      const repIndex = orderedMembers.findIndex(
        (m) => m.businessMasterId === fetched.businessMasterId,
      )
      if (repIndex > 0) {
        const [rep] = orderedMembers.splice(repIndex, 1)
        orderedMembers.unshift(rep)
      }
      // validFrom / validTo は google.type.Date (proto) → form 用 object
      const validFromDate =
        fetched.validFrom !== undefined
          ? {
              year: fetched.validFrom.year,
              month: fetched.validFrom.month,
              day: fetched.validFrom.day,
            }
          : undefined
      const validToDate =
        fetched.validTo !== undefined
          ? {
              year: fetched.validTo.year,
              month: fetched.validTo.month,
              day: fetched.validTo.day,
            }
          : undefined
      // maxReceivingPowerKw は Decimal → string (form input は string で受け取る)
      const maxKw = decimalToNumber(fetched.maxReceivingPowerKw)
      reset({
        plantCode: fetched.plantCode,
        plantName: fetched.plantName,
        plantNameAbbr: fetched.plantNameAbbr,
        areaCode: fetched.areaCode,
        powerType: fetched.powerType,
        voltageClass: fetched.voltageClass,
        maxReceivingPowerKw: maxKw !== undefined ? String(maxKw) : '',
        businessMasterId: fetched.businessMasterId,
        contractIdNo2: fetched.contractIdNo2 ?? '',
        priority: fetched.priority,
        proRataPriority: fetched.proRataPriority,
        validFrom: validFromDate ?? todayCivilDate(),
        validTo: validToDate,
        note: fetched.note ?? '',
        members: orderedMembers,
      } as PlantMasterFormInput)
    }
  }, [fetched, reset])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending || isSubmitting}
      isSaveDisabled={isFetching || isError}
      onClose={() => {
        if (!isSubmitting) handleClose()
      }}
      serverError={serverError}
      // design (`design/admin/occto.html` #plant-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t('page:adminOcctoMaster.plantMaster.modal.editTitle')}
    >
      {isError && (
        <Alert
          status="error"
          title={mapMasterGetError(
            t,
            error,
            'page:adminOcctoMaster.plantMaster.serverError',
          )}
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isFetching ? null : (
          <PlantMasterFormFields
            control={control}
            disabled={isPending || isSubmitting}
            errors={errors}
            isEdit
            setValue={setValue}
          />
        )}
      </RegionLoading>
      {confirmation !== undefined && (
        <AreaChangeConfirmDialog
          confirmation={confirmation}
          isLoading={isPending}
          onCancel={cancelConfirmation}
          onConfirm={confirm}
        />
      )}
    </CrudModalLayout>
  )
}
